import {
  CtaButton,
  CtaLink,
  ctaVariants,
  HeaderTextBlock,
  Hero,
  ImageTextBlock,
  PageSection,
  PageSectionContent,
} from '@greystar/designsystem';
import { IconName, ImageAndTextBlockProps, ResponsiveImage, SearchBoxCsr } from '@greystar/ui';
import {
  Link as JssLink,
  Text as JssText,
  useSitecoreContext,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { useEffect, useState } from 'react';

import {
  buildInstantProducts,
  buildStandaloneSearchBox,
  InstantProducts as HeadlessInstantProducts,
  SearchBox as HeadlessSearchBox,
} from '@coveo/headless/commerce';
import {
  getDictionaryEntry,
  getEngine,
  getLinkIcon,
  getTopColour,
  getWindowLocation,
  useAnalytics,
  useFormattedAddress,
  SEARCHBOX_SUBTITLE_FALLBACK,
} from '@greystar/lib';
import { EventProperty } from '@greystar/lib/src/types/property-types';
import { useI18n } from 'next-localization';

/**
 * @param props - The properties for the ImageAndTextBlock component.
 * @param props.fields - The fields provided by the datasource.
 * @param props.fields.heading - The main heading text.
 * @param props.fields.text - The text to displau.
 * @param props.fields.image - The image to display.
 * @param props.fields.cta - The call-to-action link.
 *
 * @returns A JSX element rendering the ImageAndTextBlock.
 */
export const ImageAndTextBlock = (props: ImageAndTextBlockProps): JSX.Element => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSearchButton, setIsSearchButton] = useState(false);
  const { t } = useI18n();
  const analytics = useAnalytics();

  const {
    ctaVariant,
    iconList,
    iconPosition,
    showSearchButton,
    imageBorderColor,
    imageBorderType,
    imageAspectRatio,
    imageBorderRounded,
  } = props.params;

  const { sitecoreContext } = useSitecoreContext();
  const isEditing = sitecoreContext?.pageEditing;

  const property: EventProperty = sitecoreContext?.property ?? {};
  const location = property?.location ?? {};
  const property_location = useFormattedAddress(location);

  useEffect(() => {
    if (showSearchButton === '1') {
      setIsSearchButton(true);
    } else {
      setIsSearchButton(false);
    }
  }, [showSearchButton]);

  if (!props.fields) {
    return <></>;
  }

  const searchBoxId = props.rendering.uid ?? 'searchBoxImageAndTextBlock';

  const controllerSearchBox: HeadlessSearchBox = buildStandaloneSearchBox(getEngine(), {
    options: {
      redirectionUrl: '/s',
      id: searchBoxId,
    },
  });

  const controllerInstantProducts: HeadlessInstantProducts = buildInstantProducts(getEngine(), {
    options: {
      searchBoxId: searchBoxId,
    },
  });

  const { header, text, image, cta, subTitle } = props.fields;

  const headingField = <JssText field={header} />;
  const subtitleField = <JssText field={subTitle} />;
  const textField = <JssText field={text} />;
  const linkField = <JssLink field={cta} />;
  const renderImage = (
    <ResponsiveImage image={image} fill={true} sizes="(max-width: 768px) 100vw, 50vw" />
  );
  const imagePlacement =
    props.rendering?.params?.Styles && props.rendering.params.Styles.indexOf('position-left') >= 0
      ? 'normal'
      : 'reverse';

  // type inference for cta variant
  type CtaVariantKey = keyof typeof ctaVariants;
  const rawKey = ctaVariant as CtaVariantKey;
  const ctaVariantType = ctaVariants[rawKey] ?? ctaVariants.secondary;

  // type inference + fallback for cta icon position
  let position: 'left' | 'right' = 'left';
  if (iconPosition) {
    position = iconPosition.toLowerCase() === 'left' ? 'left' : 'right';
  }

  const ctaIcon = iconList ? iconList.replace(/\s+/g, '') : undefined;

  const pushDataLayerEvent = (
    event: string,
    additionalData: Partial<{
      actionType: string;
      state?: string;
      property_location?: string;
      property_id: string;
      property_name: string;
      module_name?: string;
      inpage_location?: string;
      click_text?: string;
      click_type?: string;
      destination_url: string;
    }> = {}
  ) => {
    const eventData = {
      event: event,
      action_type: additionalData.actionType,
      state: location?.state ?? '',
      property_location: property_location,
      property_id: property.id ?? '',
      property_name: property.name ?? '',
      module_name: additionalData.module_name ?? '',
      inpage_location: additionalData.inpage_location ?? '',
      click_text: additionalData.click_text ?? '',
      click_type: additionalData.click_type ?? 'internal',
      destination_url: additionalData.destination_url ?? '',
    };
    analytics.trackEvent(event, eventData);
  };

  const handleModalClick = () => {
    setIsModalOpen((prev) => !prev);
    if (!isModalOpen) {
      pushDataLayerEvent('property_search_entry', {
        actionType: 'property search entry',
        module_name: props.fields?.header.value ?? '',
        inpage_location: 'middle',
        click_text: cta?.value?.text ?? 'Search',
        destination_url: getWindowLocation(),
      });
    }
  };

  const handleExploreMoreClick = () => {
    pushDataLayerEvent('image_cta_interaction', {
      actionType: 'Explore cta',
      module_name: props.fields?.header.value ?? '',
      inpage_location: 'middle',
      click_text: cta?.value?.text ?? 'Explore More',
      destination_url: cta?.value?.href ?? '',
    });
  };

  const ctaLink = {
    label: cta?.value?.text ?? 'CTA',
    href: cta?.value?.href ?? '/',
    variant: ctaVariantType,
    icon: getLinkIcon({ iconName: ctaIcon, iconPosition: position, variant: ctaVariantType }),
  };

  const ctaSearch = {
    label: cta?.value?.text ?? 'CTA',
    variant: ctaVariantType,
    icon: { name: 'Search' as IconName, position },
    onClick: handleModalClick,
  };

  const topColour = getTopColour(props.params?.Styles);
  const headingStyles = props.params.headingStyles as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

  return (
    <PageSection data-top-colour={topColour} topColour={topColour}>
      <PageSectionContent>
        <ImageTextBlock direction={imagePlacement}>
          <ImageTextBlock.Content>
            <Hero hasPadding={false}>
              <Hero.HeaderTextBlock direction={imagePlacement}>
                <HeaderTextBlock.Header as={headingStyles} header={headingField} />
                <HeaderTextBlock.Subtitle subtitle={subtitleField} />
              </Hero.HeaderTextBlock>
              <Hero.BodyCopy>{textField}</Hero.BodyCopy>
              <Hero.Ctas>
                {isSearchButton ? (
                  <CtaButton
                    label={ctaSearch.label}
                    variant={ctaSearch?.variant}
                    icon={ctaSearch?.icon}
                    onClick={handleModalClick}
                  >
                    {linkField}
                  </CtaButton>
                ) : (
                  <CtaLink
                    aria-label={header?.value ? 'Learn more about ' + header?.value : 'Learn more'}
                    isEditable={isEditing}
                    label={ctaLink.label}
                    href={ctaLink?.href}
                    variant={ctaLink?.variant}
                    icon={ctaLink?.icon}
                    onClick={handleExploreMoreClick}
                  >
                    {linkField}
                  </CtaLink>
                )}
              </Hero.Ctas>
            </Hero>
          </ImageTextBlock.Content>
          <ImageTextBlock.Image
            borderColour={imageBorderColor}
            borderRounded={imageBorderRounded === '1'}
            borderType={imageBorderType}
            aspectRatio={imageAspectRatio ? imageAspectRatio.replace('-', ':') : '4:3'}
          >
            {renderImage}
          </ImageTextBlock.Image>
        </ImageTextBlock>
        {isModalOpen && (
          <SearchBoxCsr
            variant="modal"
            headerTitle={getDictionaryEntry(t('searchbox_title'), 'Search for homes')}
            headerSubtitle={getDictionaryEntry(
              t('searchbox_subtitle'),
              SEARCHBOX_SUBTITLE_FALLBACK
            )}
            onClose={handleModalClick}
            searchPromptText={getDictionaryEntry(t('searchbox_prompttext'), 'Search for homes')}
            searchResultsTitle=""
            controllerSearchBox={controllerSearchBox}
            controllerInstantProducts={controllerInstantProducts}
          />
        )}
      </PageSectionContent>
    </PageSection>
  );
};

export default withDatasourceCheck()<ImageAndTextBlockProps>(ImageAndTextBlock);
